import { api, type InvestorType, type QpClientFlow, type QpFlowOption } from "../../api";

// Typed client for the embedded-Quantfolio API (/api/quantfolio/embed, M13).
// Lives beside the feature rather than in web/src/api.ts so the shared client
// stays untouched; it reuses the same `api` helper (credentials + CSRF header).

export type EmbedMode = "new" | "resume";
export type EmbedState = "open" | "closed" | "failed";

export interface EmbedSession {
  id: number;
  investor_id: number | null;
  qp_client_id: string | null;
  flow_id: string | null;
  flow_name: string | null;
  flow_instance_id: string | null;
  mode: EmbedMode;
  embed_url: string | null;
  embeddable: 0 | 1 | null;
  embed_block: string | null;
  qp_status: string | null;
  qp_external_status: string | null;
  state: EmbedState;
  actor: string | null;
  error: string | null;
  created_at: string;
  updated_at: string;
  investor_name: string | null;
  investor_type: InvestorType | null;
}

export interface EmbedPdf {
  id: string;
  name: string;
  url: string | null;
}

const BASE = "/api/quantfolio/embed";

export const embedApi = {
  flows: () => api.get<{ flows: QpFlowOption[] }>(`${BASE}/flows`),
  instances: (investorId: number) =>
    api.get<{ linked: boolean; qpClientId: string | null; instances: QpClientFlow[] }>(
      `${BASE}/investors/${investorId}/instances`,
    ),
  start: (
    body: { investorId: number; flowId: string } | { investorId: number; flowInstanceId: string },
  ) => api.post<{ session: EmbedSession }>(`${BASE}/sessions`, body),
  sessions: (investorId?: number) =>
    api.get<{ sessions: EmbedSession[] }>(
      investorId ? `${BASE}/sessions?investorId=${investorId}` : `${BASE}/sessions`,
    ),
  session: (id: number) =>
    api.get<{ session: EmbedSession; pdfs: EmbedPdf[]; refreshError: string | null }>(
      `${BASE}/sessions/${id}`,
    ),
  close: (id: number) => api.post<{ session: EmbedSession }>(`${BASE}/sessions/${id}/close`, {}),
};

/** "in_progress" → "in progress" for pill labels. */
export function statusLabel(s: string): string {
  return s.replace(/_/g, " ");
}

/** CSS-safe pill modifier from a QP status (only known values get a colour). */
export function statusClass(s: string | null | undefined): string {
  return s && /^[a-z_]+$/.test(s) ? `fstatus-${s}` : "fstatus-draft";
}

export function formatDate(iso: string | null | undefined): string {
  if (!iso) return "—";
  // SQLite datetime('now') is UTC without a zone marker; QP returns ISO-ish.
  const d = new Date(/[zZ]|[+-]\d\d:?\d\d$/.test(iso) ? iso : iso.replace(" ", "T") + "Z");
  return Number.isNaN(d.getTime())
    ? iso
    : d.toLocaleString("en-GB", { dateStyle: "medium", timeStyle: "short" });
}
